import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, Printer, Edit2, Save, StickyNote, Loader2, AlertCircle, CheckCircle, History } from 'lucide-react';
import config from '../../config/config';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import useFastImageCopy from '../../hooks/useFastImageCopy';
import DesenKritikModal from './DesenKritikModal';
import DesenRevizeGecmisiModal from './DesenRevizeGecmisiModal';
import { ConfirmationModal } from './shared/ConfirmationModal';
import BaskiGorselGalleryModal from './BaskiGorselGalleryModal';
import { BaskiGorselComponent } from './BaskiGorselLazyImage';
import {
  normalizeBaskiGorselUrls,
  resolveBaskiGorselUrl,
  getFirstBaskiGorselUrl
} from '../../utils/baskiGorselUtils';

const SiparisDetayModal = ({ isOpen, onClose, siparis, isPublicPage = false, onUpdate }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [galleryState, setGalleryState] = useState({
    open: false,
    urls: [],
    title: '',
    index: 0
  });
  const [isEditingIsletmeNotu, setIsEditingIsletmeNotu] = useState(false);
  const [editedIsletmeNotu, setEditedIsletmeNotu] = useState('');
  const [savingIsletmeNotu, setSavingIsletmeNotu] = useState(false);
  const modalRef = useRef(null);
  
  // Desen onay modal'ları state'leri
  const [showKritikModal, setShowKritikModal] = useState(false);
  const [showRevizeGecmisiModal, setShowRevizeGecmisiModal] = useState(false);
  const [selectedDesen, setSelectedDesen] = useState(null);
  const [showOnayConfirmation, setShowOnayConfirmation] = useState(false);
  const [desenToApprove, setDesenToApprove] = useState(null);
  
  // Zoom ve pan için state'ler
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [touchStartZoom, setTouchStartZoom] = useState(1);
  const imageRef = useRef(null);

  // İşletme notunu yükle
  useEffect(() => {
    if (siparis) {
      setEditedIsletmeNotu(siparis.isletme_notu || '');
    }
  }, [siparis]);

  useEffect(() => {
    if (!isOpen) return;
    setLogoLoading(true);
    setCompanyLogo('/company-logo.jpg'); // Using static logo
    setLogoLoading(false);
  }, [isOpen]);

  // Modal açıldığında body scroll'unu engelle ve arka plan etkileşimlerini engelle
  useEffect(() => {
    if (isOpen) {
      const scrollLockY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollLockY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.touchAction = 'none';

      // Arka plandaki tüm içeriğe pointer-events: none ekle
      // Bu sayede mouse hareketleri ve tıklamalar arka plana gitmez
      const rootElement = document.getElementById('root');
      if (rootElement) {
        // Root element'in kendisine pointer-events: none ekle
        // Modal overlay'i z-50 ile üstte olduğu için etkilenmez
        rootElement.style.pointerEvents = 'none';
        rootElement.style.touchAction = 'none'; // Mobil dokunma hareketlerini engelle
        rootElement.setAttribute('data-modal-open', 'true');
      }

      // Tüm dokunma olaylarını yakala ve arka plana gitmesini engelle
      const handleTouchStart = (e) => {
        // Modal overlay'i dışındaki tüm dokunmaları engelle
        const target = e.target;
        const isModalContent = target.closest('[class*="z-50"]') || target.closest('[class*="z-60"]');
        if (!isModalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      const handleTouchMove = (e) => {
        const target = e.target;
        const isModalContent = target.closest('[class*="z-50"]') || target.closest('[class*="z-60"]');
        if (!isModalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      document.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.touchAction = '';

        const rootElementCleanup = document.getElementById('root');
        if (rootElementCleanup && rootElementCleanup.getAttribute('data-modal-open') === 'true') {
          rootElementCleanup.style.pointerEvents = '';
          rootElementCleanup.style.touchAction = '';
          rootElementCleanup.removeAttribute('data-modal-open');
        }

        document.removeEventListener('touchstart', handleTouchStart, { capture: true });
        document.removeEventListener('touchmove', handleTouchMove, { capture: true });

        window.scrollTo(0, scrollLockY);
      };
    }
    return undefined;
  }, [isOpen]);

  // Resim büyütüldüğünde zoom ve pan'ı sıfırla
  useEffect(() => {
    if (enlargedImage) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
      setTouchStartDistance(null);
    }
  }, [enlargedImage]);

  // Pastel: sipariş önizleme modalı her açıldığında tam ekran resim state'ini sıfırla.
  // NOT: enlargedImage bağımlılıkta OLMAMALI — yoksa resme tıklanınca setState hemen bu effect ile silinir.
  useEffect(() => {
    if (!isOpen || !isPublicPage) return;
    setEnlargedImage(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [isOpen, isPublicPage]);

  // Wheel event listener'ı passive: false ile ekle (preventDefault için)
  useEffect(() => {
    if (!enlargedImage || !imageRef.current) return;

    const wheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Mouse wheel ile zoom
      const delta = e.deltaY > 0 ? -1 : 1;
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const imageCenterX = rect.left + rect.width / 2;
        const imageCenterY = rect.top + rect.height / 2;
        
        const mouseX = e.clientX || imageCenterX;
        const mouseY = e.clientY || imageCenterY;
        
        setZoom(prevZoom => {
          const zoomFactor = delta > 0 ? 1.1 : 0.9;
          const newZoom = Math.max(1, Math.min(5, prevZoom * zoomFactor));
          
          if (newZoom !== prevZoom) {
            setPan(prevPan => {
              const zoomChange = newZoom / prevZoom;
              const newPanX = prevPan.x - (mouseX - imageCenterX - prevPan.x) * (zoomChange - 1);
              const newPanY = prevPan.y - (mouseY - imageCenterY - prevPan.y) * (zoomChange - 1);
              return { x: newPanX, y: newPanY };
            });
          }
          
          return newZoom;
        });
      }
    };

    const element = imageRef.current;
    element.addEventListener('wheel', wheelHandler, { passive: false });

    return () => {
      element.removeEventListener('wheel', wheelHandler);
    };
  }, [enlargedImage]);

  const siparisNo = siparis?.siparis_no || '';

  const { preparing, copyImage, shareWhatsApp, printImage } = useFastImageCopy({
    isOpen,
    ready: !!siparis,
    targetRef: modalRef,
    fileName: `siparis-${siparisNo || 'preview'}.png`,
    successMessage: 'Sipariş panoya kopyalandı',
    printTitle: `Sipariş Detayı - ${siparisNo}`,
    shareTitle: 'Baskı Siparişi',
    shareText: `Sipariş No: ${siparisNo}`
  });

  if (!isOpen || !siparis) return null;

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    e.stopPropagation();
    
    if (e.touches.length === 2) {
      // İki parmakla pinch-to-zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setTouchStartDistance(distance);
      setTouchStartZoom(zoom);
    } else if (e.touches.length === 1 && zoom > 1) {
      // Tek parmakla pan
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
    
    if (e.touches.length === 2) {
      // İki parmakla pinch-to-zoom
      e.preventDefault(); // Sadece pinch-to-zoom sırasında preventDefault
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      if (touchStartDistance !== null) {
        const zoomChange = distance / touchStartDistance;
        const newZoom = Math.max(1, Math.min(5, touchStartZoom * zoomChange));
        setZoom(newZoom);
      }
    } else if (e.touches.length === 1 && isDragging && zoom > 1) {
      // Tek parmakla pan
      e.preventDefault();
      setPan({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    setIsDragging(false);
    setTouchStartDistance(null);
  };

  const processImageUrl = (gorselUrls) => {
    const first = getFirstBaskiGorselUrl(gorselUrls);
    return resolveBaskiGorselUrl(first);
  };

  const openDesenGallery = (varyant, startIndex = 0) => {
    const urls = normalizeBaskiGorselUrls(
      varyant?.desen?.gorsel_urls || siparis?.gorsel_urls || []
    );
    if (urls.length === 0) return;
    setGalleryState({
      open: true,
      urls,
      title: varyant?.desen?.desen_no || varyant?.siparis_varyant_adi || siparis?.siparis_no || 'Desen görselleri',
      index: startIndex
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      style={{ 
        pointerEvents: 'auto',
        touchAction: 'none' // Mobil dokunma hareketlerini tamamen kontrol et
      }}
      onClick={(e) => {
        // Public page veya büyütülmüş resim açıkken overlay'e tıklama devre dışı
        if (isPublicPage || enlargedImage || galleryState.open) {
          return;
        }
        e.stopPropagation();
        onClose();
      }}
          onTouchStart={(e) => {
            // Tüm dokunma olaylarını yakala ve arka plana gitmesini engelle
            e.stopPropagation();
            // Overlay'e dokunulduğunda modal'ı kapat (sadece public page değilse ve büyütülmüş resim açık değilse)
            if (!isPublicPage && !enlargedImage && e.target === e.currentTarget) {
              onClose();
            }
          }}
      onTouchMove={(e) => {
        // Dokunma hareketlerini yakala ve arka plana gitmesini engelle
        e.stopPropagation();
        e.preventDefault(); // Varsayılan scroll davranışını engelle
      }}
      onTouchEnd={(e) => {
        // Dokunma bitişini yakala ve arka plana gitmesini engelle
        e.stopPropagation();
      }}
    >
      {/* Modal container */}
      <div 
        className="relative w-full max-w-4xl"
        style={{ touchAction: 'auto' }} // Modal içeriği dokunma hareketlerine izin ver
        onClick={(e) => {
          // Modal içeriğine tıklandığında modal'ın kapanmasını engelle
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Modal içeriğine dokunulduğunda modal'ın kapanmasını engelle
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Modal içeriğindeki dokunma hareketlerini yakala
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // Modal içeriğindeki dokunma bitişini yakala
          e.stopPropagation();
        }}
      >
        {/* Action buttons - Sadece public page değilse göster */}
        {!isPublicPage && (
          <div className="flex justify-end mb-2 gap-1">
            <button
              onClick={copyImage}
              disabled={preparing}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={preparing ? 'Kopyalanıyor...' : 'Panoya Kopyala'}
            >
              {preparing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
            
            <button
              onClick={printImage}
              className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-lg"
              title="Yazdır"
            >
              <Printer className="h-4 w-4" />
            </button>
            
            <button
              onClick={shareWhatsApp}
              className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-lg"
              title="WhatsApp'ta Paylaş"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </button>

            <button
              onClick={onClose}
              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg"
              title="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Modal içeriği - Sadece bu kısım kopyalanır */}
        <div ref={modalRef} className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-h-[95vh] overflow-y-auto relative">
          {/* Kapatma butonu - Public page için modal içeriğinin sağ üstünde */}
          {isPublicPage && (
            <button
              onClick={() => {
                // Önce resmi kapat (eğer açıksa), sonra modalı kapat
                if (enlargedImage) {
                  setEnlargedImage(null);
                  setZoom(1);
                  setPan({ x: 0, y: 0 });
                }
                onClose();
              }}
              className="absolute top-3 right-3 p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg"
              style={{ zIndex: enlargedImage ? 40 : 30 }}
              title="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          
          {/* Header */}
          <div className="relative border-b border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              {/* Left: Logo */}
              <div className="h-16 flex items-center">
                {logoLoading ? (
                  <div className="h-16 w-24 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    Yükleniyor...
                  </div>
                ) : companyLogo ? (
                  <img
                    src={companyLogo}
                    alt="Logo"
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="h-16 w-24 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    LOGO
                  </div>
                )}
                {/* Fallback placeholder */}
                <div className="hidden h-16 w-24 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                  LOGO
                </div>
              </div>

              {/* Center: Title */}
              <h2 className="absolute left-1/2 -translate-x-1/2 text-lg sm:text-xl font-bold tracking-wide text-gray-900 dark:text-white">
                BASKI SİPARİŞİ
              </h2>

              {/* Right: Date */}
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {siparis.siparis_tarihi ? new Date(siparis.siparis_tarihi).toLocaleDateString('tr-TR') : ''}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 sm:p-6 space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{siparis.siparis_no}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{siparis.musteri_adi}</div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                İşletme: <span className="text-sm font-medium text-gray-900 dark:text-white">{siparis.isletme_adi || 'Belirtilmemiş'}</span>
              </div>
            </div>
          </div>

          {/* Varyantlar */}
          <div className="px-4 sm:px-6 pb-4">
            {siparis.varyantlar && siparis.varyantlar.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {siparis.varyantlar.map((varyant, index) => {
                  const desenUrls = normalizeBaskiGorselUrls(
                    varyant.desen?.gorsel_urls || siparis.gorsel_urls || []
                  );
                  const imageUrl = processImageUrl(desenUrls);
                  
                  return (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 relative">
                      {/* Varyant Resmi — kartta tek görsel; tıklanınca tümü */}
                      <div className="flex justify-center mb-4 relative">
                        {imageUrl ? (
                          <>
                            <div
                              className="relative w-full max-w-xs h-64 rounded-lg overflow-hidden shadow-sm cursor-pointer hover:opacity-90 transition-opacity bg-gray-100 dark:bg-gray-700"
                              onClick={() => openDesenGallery(varyant, 0)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') openDesenGallery(varyant, 0);
                              }}
                            >
                              <BaskiGorselComponent
                                imageUrl={desenUrls[0]}
                                alt={varyant.siparis_varyant_adi || varyant.desen?.desen_no || 'Desen'}
                                useThumbnail
                                thumbnailSize={400}
                                priority={false}
                              />
                              {desenUrls.length > 1 && (
                                <span className="absolute bottom-2 left-2 z-20 inline-flex px-2 py-1 text-[11px] font-semibold rounded-full bg-black/70 text-white">
                                  +{desenUrls.length - 1} görsel
                                </span>
                              )}
                            </div>
                            {/* Desen Onay Durumu Badge - Sağ Alt */}
                            {varyant.desen?.onay_durumu && (
                              <div className="absolute bottom-2 right-2 z-20">
                                <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full shadow-lg ${
                                  varyant.desen.onay_durumu === 'onay_bekliyor' 
                                    ? 'bg-yellow-500/90 text-yellow-900 dark:bg-yellow-600/90 dark:text-yellow-100' :
                                  varyant.desen.onay_durumu === 'revize' 
                                    ? 'bg-red-500/90 text-red-900 dark:bg-red-600/90 dark:text-red-100' :
                                  varyant.desen.onay_durumu === 'onayli' 
                                    ? 'bg-green-500/90 text-green-900 dark:bg-green-600/90 dark:text-green-100' :
                                    'bg-gray-500/90 text-gray-900 dark:bg-gray-600/90 dark:text-gray-100'
                                }`}>
                                  {varyant.desen.onay_durumu === 'onay_bekliyor' ? 'Onay Bekliyor' :
                                   varyant.desen.onay_durumu === 'revize' ? 'Revize' :
                                   varyant.desen.onay_durumu === 'onayli' ? 'Onaylı' : ''}
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-64 h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400">
                            Resim Yok
                          </div>
                        )}
                      </div>

                      {/* Varyant Bilgileri */}
                      <div className="text-center space-y-2">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {varyant.desen?.desen_no || varyant.siparis_varyant_adi || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {Math.round(parseFloat(varyant.metre || 0))} m
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Kumaş: {siparis.stokKart?.name || 'Belirtilmemiş'}
                        </div>
                        
                        {/* Desen Onay Butonları */}
                        {varyant.desen?.id && varyant.desen?.onay_durumu && 
                         (varyant.desen.onay_durumu === 'onay_bekliyor' || varyant.desen.onay_durumu === 'revize') && (
                          <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => {
                                setSelectedDesen({
                                  id: varyant.desen.id,
                                  desen_no: varyant.desen.desen_no
                                });
                                setShowKritikModal(true);
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                              title="Kritik Ekle"
                            >
                              <AlertCircle className="h-3 w-3" />
                              <span>Kritik</span>
                            </button>
                            <button
                              onClick={() => {
                                setDesenToApprove({
                                  id: varyant.desen.id,
                                  desen_no: varyant.desen.desen_no
                                });
                                setShowOnayConfirmation(true);
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                              title="Onayla"
                            >
                              <CheckCircle className="h-3 w-3" />
                              <span>Onayla</span>
                            </button>
                            {varyant.desen.revizeler && varyant.desen.revizeler.length > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedDesen({
                                    id: varyant.desen.id,
                                    desen_no: varyant.desen.desen_no
                                  });
                                  setShowRevizeGecmisiModal(true);
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                title="Revize Geçmişi"
                              >
                                <History className="h-3 w-3" />
                                <span>Geçmiş</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                Varyant bulunamadı
              </div>
            )}
          </div>

          {/* Notlar Bölümü */}
          <div className="px-4 sm:px-6 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              Notlar
            </h3>
            
            {/* Sipariş Notu - Public sayfada gözükmeyecek */}
            {!isPublicPage && siparis.notlar && (
              <div className="mb-4">
                <div className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sipariş Notu
                </div>
                <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 rounded-md p-3 whitespace-pre-wrap">
                  {siparis.notlar}
                </div>
              </div>
            )}

            {/* İşletme Notu - Public sayfada her zaman göster */}
            <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    İşletme Notu
                  </div>
                  {isPublicPage && (
                    <button
                      onClick={() => {
                        setIsEditingIsletmeNotu(!isEditingIsletmeNotu);
                        setEditedIsletmeNotu(siparis.isletme_notu || '');
                      }}
                      className="p-1 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                      title={isEditingIsletmeNotu ? 'İptal' : 'Düzenle'}
                    >
                      {isEditingIsletmeNotu ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <Edit2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
                {isPublicPage && isEditingIsletmeNotu ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedIsletmeNotu}
                      onChange={(e) => setEditedIsletmeNotu(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="İşletme notu girin..."
                      rows={3}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={async () => {
                          setSavingIsletmeNotu(true);
                          try {
                            const response = await api.put(
                              `/api/public/pastel/isletme-notu/${siparis.id}/x8k9m2p4q8r3n5w6v`,
                              { isletme_notu: editedIsletmeNotu }
                            );
                            if (response.data.success) {
                              toast.success('İşletme notu güncellendi');
                              setIsEditingIsletmeNotu(false);
                              if (onUpdate) {
                                onUpdate();
                              }
                              // Sipariş verisini güncelle
                              siparis.isletme_notu = editedIsletmeNotu;
                            } else {
                              toast.error('İşletme notu güncellenemedi');
                            }
                          } catch (error) {
                            console.error('İşletme notu güncelleme hatası:', error);
                            toast.error('İşletme notu güncellenemedi');
                          } finally {
                            setSavingIsletmeNotu(false);
                          }
                        }}
                        disabled={savingIsletmeNotu}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-md disabled:opacity-50 transition-colors flex items-center gap-1"
                      >
                        <Save className="h-3 w-3" />
                        {savingIsletmeNotu ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 rounded-md p-3 whitespace-pre-wrap min-h-[60px]">
                    {siparis.isletme_notu || (isPublicPage ? 'Henüz not eklenmemiş. Düzenle butonuna tıklayarak not ekleyebilirsiniz.' : 'Not eklenmemiş')}
                  </div>
                )}
              </div>
            </div>
        </div>
      </div>

      {/* Büyütülmüş Resim Modalı */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
          style={{ 
            pointerEvents: 'auto',
            touchAction: 'none', // Mobil dokunma hareketlerini tamamen kontrol et
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onClick={(e) => {
            // Resim overlay'ine tıklama - sadece resmi kapat, modalı kapatma
            e.stopPropagation();
            e.preventDefault();
            if (zoom === 1) {
              setEnlargedImage(null);
              setZoom(1);
              setPan({ x: 0, y: 0 });
              // Ana modala odaklan
              setTimeout(() => {
                if (modalRef.current) {
                  modalRef.current.focus();
                  modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
          }}
          onTouchStart={(e) => {
            // Resim overlay'ine dokunma - sadece resmi kapat, modalı kapatma
            e.stopPropagation();
            e.preventDefault();
            if (e.target === e.currentTarget && zoom === 1) {
              setEnlargedImage(null);
              setZoom(1);
              setPan({ x: 0, y: 0 });
              // Ana modala odaklan
              setTimeout(() => {
                if (modalRef.current) {
                  modalRef.current.focus();
                  modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
          }}
          onTouchMove={(e) => {
            // Dokunma hareketlerini yakala ve arka plana gitmesini engelle
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            // Dokunma bitişini yakala ve arka plana gitmesini engelle
            e.stopPropagation();
          }}
        >
          <div 
            className="relative max-w-4xl max-h-full overflow-hidden"
            style={{ touchAction: 'none' }} // Resim içeriği dokunma hareketlerini kontrol et
            onClick={(e) => {
              e.stopPropagation();
              if (zoom === 1) {
                setEnlargedImage(null);
              }
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              ref={imageRef}
              src={enlargedImage}
              alt="Büyütülmüş Resim"
              className="rounded-lg shadow-2xl select-none"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: isDragging || touchStartDistance !== null ? 'none' : 'transform 0.1s ease-out',
                maxWidth: '100%',
                maxHeight: '100vh',
                objectFit: 'contain',
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              draggable={false}
            />
            {/* Resim kapatma butonu - Sadece resmi kapatır, modalı kapatmaz */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation();
                // Sadece resmi kapat, modalı açık bırak
                setEnlargedImage(null);
                setZoom(1);
                setPan({ x: 0, y: 0 });
                // Ana modala odaklan
                setTimeout(() => {
                  if (modalRef.current) {
                    modalRef.current.focus();
                    modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation();
                // Sadece resmi kapat, modalı açık bırak
                setEnlargedImage(null);
                setZoom(1);
                setPan({ x: 0, y: 0 });
                // Ana modala odaklan
                setTimeout(() => {
                  if (modalRef.current) {
                    modalRef.current.focus();
                    modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
              className="absolute top-4 right-4 p-3 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-lg"
              style={{ zIndex: 10002, pointerEvents: 'auto' }}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
      
      {/* Desen Kritik Modal */}
      {selectedDesen && (
        <DesenKritikModal
          isOpen={showKritikModal}
          onClose={() => {
            setShowKritikModal(false);
            setSelectedDesen(null);
          }}
          desenId={selectedDesen.id}
          desenNo={selectedDesen.desen_no}
          isPublic={isPublicPage}
          onSuccess={() => {
            onUpdate && onUpdate();
          }}
        />
      )}
      
      {/* Desen Revize Geçmişi Modal */}
      {selectedDesen && (
        <DesenRevizeGecmisiModal
          isOpen={showRevizeGecmisiModal}
          onClose={() => {
            setShowRevizeGecmisiModal(false);
            setSelectedDesen(null);
          }}
          desenId={selectedDesen.id}
          desenNo={selectedDesen.desen_no}
          isPublic={isPublicPage}
        />
      )}

      {/* Onay Confirmation Modal */}
      {showOnayConfirmation && desenToApprove && (
        <ConfirmationModal
          title="Deseni Onayla"
          message={`"${desenToApprove.desen_no}" desenini onaylamak istediğinize emin misiniz?`}
          confirmText="Evet, Onayla"
          cancelText="İptal"
          confirmStyle="primary"
          onConfirm={async () => {
            try {
              const { onaylaDesenPublic, onaylaDesen } = await import('../../services/desenService');
              const onaylaFn = isPublicPage ? onaylaDesenPublic : onaylaDesen;
              await onaylaFn(desenToApprove.id);
              toast.success('Desen onaylandı');
              setShowOnayConfirmation(false);
              setDesenToApprove(null);
              onUpdate && onUpdate();
            } catch (error) {
              console.error('Onaylama hatası:', error);
              toast.error(error.response?.data?.message || 'Onaylama başarısız');
            }
          }}
          onCancel={() => {
            setShowOnayConfirmation(false);
            setDesenToApprove(null);
          }}
        />
      )}

      <BaskiGorselGalleryModal
        isOpen={galleryState.open}
        onClose={() => setGalleryState({ open: false, urls: [], title: '', index: 0 })}
        title={galleryState.title}
        gorselUrls={galleryState.urls}
        initialIndex={galleryState.index}
        zIndexClass="z-[80]"
      />
    </div>
  );
};

export default SiparisDetayModal;
